import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFileInvoice, 
  faUsers, 
  faBoxes, 
  faChartBar, 
  faCog,
  faMoneyBill,
  faTruck,
  faShoppingCart,
  faFileAlt,
  faDatabase,
  faUserShield,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import './TabNavigator.css';

const TabNavigator = ({ activeTab, onTabChange, visibleTabs }) => {
  const [expandedGroup, setExpandedGroup] = useState(() => {
    // Determine which group should be expanded based on activeTab
    if (['quotations', 'invoices', 'purchases', 'expenses', 'claims'].includes(activeTab)) return 'documents';
    if (['customers', 'products', 'vendors'].includes(activeTab)) return 'data';
    if (['reports', 'users', 'admin'].includes(activeTab)) return 'administration';
    return 'documents';
  });

  const tabGroups = [
    {
      id: 'documents',
      label: 'Documents',
      icon: faFileAlt,
      color: '#93C5FD', // Pastel Blue
      tabs: [
        { id: 'quotations', label: 'Quotations', icon: faFileInvoice },
        { id: 'invoices', label: 'Invoices', icon: faFileInvoice },
        { id: 'purchases', label: 'Purchases', icon: faShoppingCart },
        { id: 'expenses', label: 'Expenses', icon: faMoneyBill },
        { id: 'claims', label: 'Claims', icon: faFileAlt }
      ]
    },
    {
      id: 'data',
      label: 'Data',
      icon: faDatabase,
      color: '#86EFAC', // Pastel Green
      tabs: [
        { id: 'customers', label: 'Customers', icon: faUsers },
        { id: 'products', label: 'Products', icon: faBoxes },
        { id: 'vendors', label: 'Vendors', icon: faTruck }
      ]
    },
    {
      id: 'administration',
      label: 'Administration',
      icon: faUserShield,
      color: '#FCA5A5', // Pastel Red
      tabs: [
        { id: 'reports', label: 'Reports', icon: faChartBar },
        { id: 'users', label: 'Users', icon: faUsers },
        { id: 'admin', label: 'Configuration', icon: faCog }
      ]
    }
  ];

  const visibleTabSet = new Set(visibleTabs);
  const filteredGroups = tabGroups
    .map((group) => ({
      ...group,
      tabs: group.tabs.filter((tab) => visibleTabSet.has(tab.id))
    }))
    .filter((group) => group.tabs.length > 0);

  const handleGroupClick = (groupId) => {
    if (expandedGroup === groupId) {
      setExpandedGroup(null);
    } else {
      setExpandedGroup(groupId);
    }
  };

  const handleTabClick = (tabId) => {
    onTabChange(tabId);
  };

  return (
    <div className="tab-navigator-grouped">
      <div className="tab-groups-container">
        {filteredGroups.map(group => (
          <div key={group.id} className="tab-group">
            <button
              className={`tab-group-header ${expandedGroup === group.id ? 'expanded' : ''}`}
              onClick={() => handleGroupClick(group.id)}
              style={{ '--group-color': group.color }}
            >
              <div className="tab-group-header-content">
                <FontAwesomeIcon icon={group.icon} className="tab-group-icon" />
                <span className="tab-group-label">{group.label}</span>
              </div>
              <FontAwesomeIcon 
                icon={expandedGroup === group.id ? faChevronUp : faChevronDown} 
                className="tab-group-chevron" 
              />
            </button>
          </div>
        ))}
      </div>
      
      {filteredGroups.map(group => (
        expandedGroup === group.id && (
          <div key={`items-${group.id}`} className="tab-group-items" style={{ '--group-color': group.color }}>
            {group.tabs.map(tab => (
              <button
                key={tab.id + tab.label}
                className={`tab-item ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => handleTabClick(tab.id)}
                style={{ '--tab-color': group.color }}
              >
                <FontAwesomeIcon icon={tab.icon} className="tab-icon" />
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </div>
        )
      ))}
    </div>
  );
};

export default TabNavigator;